import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Users, Clock, Home, Layers, ArrowRight, CheckCircle2, RefreshCw, X, DollarSign, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';
const data = [{
  name: 'The Lawson',
  buyers: 45
}, {
  name: 'The Samson',
  buyers: 32
}, {
  name: 'The Pine',
  buyers: 28
}, {
  name: 'The Cedar',
  buyers: 19
}, {
  name: 'The Birch',
  buyers: 12
}];
const projects = [{
  id: 1,
  name: 'The Lawson'
}, {
  id: 2,
  name: 'The Samson'
}];
const COLORS = ['#06b6d4', '#94a3b8', '#94a3b8', '#94a3b8', '#94a3b8'];
// Upgrade stats
const upgradeStats = {
  totalUpgradeRevenue: 271300,
  avgUpgradeValue: 45217,
  topUpgradeCategory: 'Kitchen'
};
export function DashboardPage() {
  const navigate = useNavigate();
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showProjectModal) {
        setShowProjectModal(false);
        setSelectedProject('');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showProjectModal]);
  const handleManageOptions = () => {
    if (!selectedProject) return;
    navigate('/options/models');
    setShowProjectModal(false);
    setSelectedProject('');
  };
  return <>
      <div className="space-y-8 max-w-7xl mx-auto" data-id="element-1635">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-1636">
          <div data-id="element-1637">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-1638">Dashboard</h1>
            <p className="text-slate-500 mt-1" data-id="element-1639">
              Welcome back, here's what's happening today.
            </p>
          </div>
          <div className="flex gap-3" data-id="element-1640">
            <Button variant="outline" onClick={() => navigate('/analytics')} data-id="element-1641">
              View Analytics
            </Button>
            <Button onClick={() => setShowProjectModal(true)} data-id="element-1642">
              Manage Options
            </Button>
          </div>
        </div>

        {/* KPI Cards - Row 1: Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1643">
          <Card data-id="element-1644">
            <CardContent className="p-6" data-id="element-1645">
              <div className="flex items-center gap-4" data-id="element-1646">
                <div className="p-3 bg-primary-50 rounded-lg flex-shrink-0" data-id="element-1647">
                  <Users className="h-6 w-6 text-primary-600" data-id="element-1648" />
                </div>
                <div className="min-w-0" data-id="element-1649">
                  <p className="text-sm font-medium text-slate-500" data-id="element-1650">
                    Active Buyers
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900" data-id="element-1651">124</h3>
                  <p className="text-xs text-green-600 mt-1" data-id="element-1652">
                    <span className="font-medium" data-id="element-1653">+12%</span>
                    <span className="ml-1 text-slate-400" data-id="element-1654">vs last month</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-1655">
            <CardContent className="p-6" data-id="element-1656">
              <div className="flex items-center gap-4" data-id="element-1657">
                <div className="p-3 bg-emerald-50 rounded-lg flex-shrink-0" data-id="element-1658">
                  <DollarSign className="h-6 w-6 text-emerald-600" data-id="element-1659" />
                </div>
                <div className="min-w-0" data-id="element-1660">
                  <p className="text-sm font-medium text-slate-500" data-id="element-1661">
                    Upgrade Revenue
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900" data-id="element-1662">$271K</h3>
                  <p className="text-xs text-green-600 mt-1" data-id="element-1663">
                    <span className="font-medium" data-id="element-1664">+18%</span>
                    <span className="ml-1 text-slate-400" data-id="element-1665">vs last month</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-1666">
            <CardContent className="p-6" data-id="element-1667">
              <div className="flex items-center gap-4" data-id="element-1668">
                <div className="p-3 bg-purple-50 rounded-lg flex-shrink-0" data-id="element-1669">
                  <TrendingUp className="h-6 w-6 text-purple-600" data-id="element-1670" />
                </div>
                <div className="min-w-0" data-id="element-1671">
                  <p className="text-sm font-medium text-slate-500" data-id="element-1672">
                    Avg Upgrade Value
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900" data-id="element-1673">$45.2K</h3>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-1674">Per customer</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-1675">
            <CardContent className="p-6" data-id="element-1676">
              <div className="flex items-center gap-4" data-id="element-1677">
                <div className="p-3 bg-slate-100 rounded-lg flex-shrink-0" data-id="element-1678">
                  <Clock className="h-6 w-6 text-slate-600" data-id="element-1679" />
                </div>
                <div className="min-w-0" data-id="element-1680">
                  <p className="text-sm font-medium text-slate-500" data-id="element-1681">
                    Total Time Spent
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900" data-id="element-1682">842h</h3>
                  <p className="text-xs text-green-600 mt-1" data-id="element-1683">
                    <span className="font-medium" data-id="element-1684">+5%</span>
                    <span className="ml-1 text-slate-400" data-id="element-1685">vs last month</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* KPI Cards - Row 2: Top Performers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1686">
          <Card data-id="element-1687">
            <CardContent className="p-6" data-id="element-1688">
              <div className="flex items-center gap-4" data-id="element-1689">
                <div className="p-3 bg-slate-100 rounded-lg flex-shrink-0" data-id="element-1690">
                  <Home className="h-6 w-6 text-slate-600" data-id="element-1691" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-1692">
                  <p className="text-sm font-medium text-slate-500" data-id="element-1693">
                    Top Model
                  </p>
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-1694">
                    The Lawson
                  </h3>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-1695">
                    45 active prospects
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-1696">
            <CardContent className="p-6" data-id="element-1697">
              <div className="flex items-center gap-4" data-id="element-1698">
                <div className="p-3 bg-slate-100 rounded-lg flex-shrink-0" data-id="element-1699">
                  <Layers className="h-6 w-6 text-slate-600" data-id="element-1700" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-1701">
                  <p className="text-sm font-medium text-slate-500" data-id="element-1702">
                    Top Option
                  </p>
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-1703">
                    Quartz Countertops
                  </h3>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-1704">
                    Selected by 82% of buyers
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1705">
          {/* Chart Section */}
          <Card className="lg:col-span-2" data-id="element-1706">
            <CardHeader data-id="element-1707">
              <CardTitle data-id="element-1708">Buyer Distribution by Model</CardTitle>
            </CardHeader>
            <CardContent data-id="element-1709">
              <div className="h-[300px] w-full" data-id="element-1710">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1711">
                  <BarChart data={data} layout="vertical" margin={{
                  top: 5,
                  right: 30,
                  left: 40,
                  bottom: 5
                }} data-id="element-1712">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" data-id="element-1713" />
                    <XAxis type="number" hide data-id="element-1714" />
                    <YAxis dataKey="name" type="category" width={100} tick={{
                    fill: '#64748b',
                    fontSize: 12
                  }} axisLine={false} tickLine={false} data-id="element-1715" />
                    <Tooltip cursor={{
                    fill: '#f1f5f9'
                  }} contentStyle={{
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }} data-id="element-1716" />
                    <Bar dataKey="buyers" radius={[0, 4, 4, 0]} barSize={32} data-id="element-1717">
                      {data.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-1718" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Status & Quick Actions */}
          <div className="space-y-6" data-id="element-1719">
            <Card data-id="element-1720">
              <CardHeader data-id="element-1721">
                <CardTitle data-id="element-1722">System Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-1723">
                <div className="flex items-center justify-between" data-id="element-1724">
                  <div className="flex items-center gap-3" data-id="element-1725">
                    <div className="h-2 w-2 rounded-full bg-green-500" data-id="element-1726"></div>
                    <span className="text-sm font-medium text-slate-700" data-id="element-1727">
                      App Connection
                    </span>
                  </div>
                  <Badge variant="success" data-id="element-1728">Healthy</Badge>
                </div>
                <div className="flex items-center justify-between" data-id="element-1729">
                  <div className="flex items-center gap-3" data-id="element-1730">
                    <div className="h-2 w-2 rounded-full bg-green-500" data-id="element-1731"></div>
                    <span className="text-sm font-medium text-slate-700" data-id="element-1732">
                      Options Sync
                    </span>
                  </div>
                  <span className="text-xs text-slate-500" data-id="element-1733">2 mins ago</span>
                </div>

                <div className="pt-4 border-t border-slate-100" data-id="element-1734">
                  <Button variant="outline" size="sm" className="w-full" leftIcon={<RefreshCw className="h-3 w-3" data-id="element-1736" />} data-id="element-1735">
                    Force Sync
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 text-white border-slate-800" data-id="element-1737">
              <CardContent className="p-6" data-id="element-1738">
                <h3 className="font-bold text-lg mb-2" data-id="element-1739">Need help?</h3>
                <p className="text-slate-400 text-sm mb-4" data-id="element-1740">
                  Our support team is available to help you configure your
                  project options.
                </p>
                <Button variant="secondary" className="w-full !bg-white !text-slate-900 hover:!bg-slate-100 !shadow-sm" onClick={() => navigate('/support')} data-id="element-1741">
                  Contact Support
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Select Project Modal */}
      {showProjectModal && <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-50" data-id="element-1742">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" data-id="element-1743">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between" data-id="element-1744">
              <div data-id="element-1745">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-1746">
                  Select Project
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-1747">
                  Choose a project to manage its options
                </p>
              </div>
              <button onClick={() => {
            setShowProjectModal(false);
            setSelectedProject('');
          }} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-1748">
                <X className="h-5 w-5" data-id="element-1749" />
              </button>
            </div>
            <div className="p-6" data-id="element-1750">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1751">
                Project
              </label>
              <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" autoFocus data-id="element-1752">
                <option value="" data-id="element-1753">Select a project...</option>
                {projects.map(project => <option key={project.id} value={project.id} data-id="element-1754">
                    {project.name}
                  </option>)}
              </select>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3" data-id="element-1755">
              <Button variant="outline" onClick={() => {
            setShowProjectModal(false);
            setSelectedProject('');
          }} data-id="element-1756">
                Cancel
              </Button>
              <Button onClick={handleManageOptions} disabled={!selectedProject} data-id="element-1757">
                Continue
              </Button>
            </div>
          </div>
        </div>}
    </>;
}